import {
    AREA_MAX_POINTS,
    AREA_MIN_POINTS,
    AREA_POLYGON_ERROR_TEXT,
    AreaPolygonError,
    GeoJsonPolygon,
    LngLat,
    openRing,
    pointInPolygon,
    toAreaPolygon,
    validateAreaPoints,
} from '@/helpers/geoPolygon.helper';

/** "Umkreis" (radius around the home address) or "Bereich zeichnen" (free polygon) */
export type SearchMode = 'radius' | 'area';

export const SEARCH_MODES: Array<{ value: SearchMode; text: string; icon: string }> = [
    {value: 'radius', text: 'Umkreis', icon: 'mdi-radius-outline'},
    {value: 'area', text: 'Bereich zeichnen', icon: 'mdi-vector-polygon'},
];

/** Area while drawing: corner points [lng, lat] without closing point; closed once the first point was clicked again */
export interface AreaDraft {
    points: LngLat[];
    closed: boolean;
}

export const emptyDraft = (): AreaDraft => ({points: [], closed: false});

/** Adds a corner point at the end; ignored once closed or at the point limit */
export function addPoint(draft: AreaDraft, point: LngLat): AreaDraft {
    if (draft.closed || draft.points.length >= AREA_MAX_POINTS) {
        return draft;
    }
    return {points: [...draft.points, point], closed: false};
}

/** Click on the first point: closes the area if it has enough points */
export function closeDraft(draft: AreaDraft): AreaDraft {
    if (draft.closed || openRing(draft.points).length < AREA_MIN_POINTS) {
        return draft;
    }
    return {points: openRing(draft.points), closed: true};
}

export function movePoint(draft: AreaDraft, index: number, point: LngLat): AreaDraft {
    if (index < 0 || index >= draft.points.length) {
        return draft;
    }
    const points = [...draft.points];
    points[index] = point;
    return {...draft, points};
}

/** Double-click on a point deletes it; a closed area with less than 3 points opens again */
export function removePoint(draft: AreaDraft, index: number): AreaDraft {
    if (index < 0 || index >= draft.points.length) {
        return draft;
    }
    const points = draft.points.filter((_, i) => i !== index);
    return {points, closed: draft.closed && points.length >= AREA_MIN_POINTS};
}

/** "Letzten Punkt entfernen" */
export function undoLast(draft: AreaDraft): AreaDraft {
    return removePoint(draft, draft.points.length - 1);
}

/** Point at a distance (meters) and bearing (degrees) from the center, on a sphere */
function destination(center: LngLat, meters: number, bearingDeg: number): LngLat {
    const R = 6371000;
    const toRad = (v: number) => v * Math.PI / 180;
    const toDeg = (v: number) => v * 180 / Math.PI;
    const lat1 = toRad(center[1]);
    const lng1 = toRad(center[0]);
    const d = meters / R;
    const b = toRad(bearingDeg);
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
    const lng2 = lng1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    const round = (v: number) => Math.round(v * 1e6) / 1e6;
    return [round(toDeg(lng2)), round(toDeg(lat2))];
}

/** "Aus Umkreis starten": the current circle as an editable, closed polygon (octagon by default) */
export function draftFromCircle(center: LngLat, radiusMeters: number, corners = 8): AreaDraft {
    const points = Array.from({length: corners}, (_, i) => destination(center, radiusMeters, i * 360 / corners));
    return {points, closed: true};
}

/** Hint for the current draft; null when it can be searched */
export function draftProblem(draft: AreaDraft): string | null {
    const count = openRing(draft.points).length;
    if (!count) {
        return 'Klicke Punkte auf die Karte, um die Fläche zu umranden.';
    }
    if (!draft.closed) {
        return count < AREA_MIN_POINTS
            ? `Noch ${AREA_MIN_POINTS - count} ${AREA_MIN_POINTS - count === 1 ? 'Punkt' : 'Punkte'}, dann den ersten Punkt anklicken.`
            : 'Klicke auf den ersten Punkt, um die Fläche zu schließen.';
    }
    const error = validateAreaPoints(draft.points);
    if (error === AreaPolygonError.SELF_INTERSECTING) {
        return 'Die Linien kreuzen sich. Verschiebe oder lösche einen Punkt, damit die Fläche sich nicht selbst schneidet.';
    }
    return error ? AREA_POLYGON_ERROR_TEXT[error] : null;
}

/** GeoJSON polygon of a closed, valid draft; otherwise null (no search, no list) */
export function draftPolygon(draft: AreaDraft): GeoJsonPolygon | null {
    if (!draft.closed || validateAreaPoints(draft.points)) {
        return null;
    }
    return toAreaPolygon(draft.points);
}

/** Stored area of a list back into an (already closed) draft */
export function draftFromPolygon(polygon?: GeoJsonPolygon | null): AreaDraft {
    const ring = polygon?.coordinates?.[0];
    return ring ? {points: openRing(ring), closed: true} : emptyDraft();
}

/** Approximate area in km² (equirectangular projection, fine for a few dozen km) */
export function areaKm2(points: LngLat[]): number {
    const ring = openRing(points);
    if (ring.length < 3) {
        return 0;
    }
    const lat0 = ring.reduce((sum, p) => sum + p[1], 0) / ring.length * Math.PI / 180;
    const kx = 111.32 * Math.cos(lat0);
    const ky = 110.574;
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[(i + 1) % ring.length];
        sum += x1 * kx * y2 * ky - x2 * kx * y1 * ky;
    }
    return Math.abs(sum / 2);
}

/** Default list name: "<Ort> + 10 km" for a radius, "<Ort> · Bereich" for a drawn area */
export function defaultListName(place: string, mode: SearchMode, radiusKm: number): string {
    return mode === 'area' ? `${place} · Bereich` : `${place} + ${radiusKm} km`;
}

type GeoRow = { _id: unknown; address?: { location?: { coordinates?: number[] } } };

/**
 * Rows of the area search: everything from the area query is inside; schools of the comparison radius that are
 * not in the area are added as "außerhalb" (deselected by default, can be selected by hand like route-outside rows).
 */
export function mergeAreaRows<T extends GeoRow>(inside: T[], radius: T[], polygon: GeoJsonPolygon): Array<T & { outsideArea: boolean }> {
    const ids = new Set(inside.map((row) => String(row._id)));
    const outside = radius.filter((row) => {
        const coords = row.address?.location?.coordinates;
        return !ids.has(String(row._id)) && !(coords && pointInPolygon([coords[0], coords[1]], polygon));
    });
    return [
        ...inside.map((row) => ({...row, outsideArea: false})),
        ...outside.map((row) => ({...row, outsideArea: true})),
    ];
}
